"use client";

/**
 * V2 Schedule Page Wrapper
 *
 * Client wrapper for the focused calendar view (reached via `?view=schedule` on the page's own
 * URL). Mounts the same provider tree the inline public view uses (PublicPageProvider →
 * BuilderContext + PublicViewContext + SchedulePublicDataProvider + toast) so CalendarView /
 * ScheduleDetailModal work unchanged, then renders only the FocusedScheduleView (no navbar, footer,
 * or live banner).
 *
 * Access control + viewer resolution happen server-side in the page route (the same gate as the
 * normal render); this wrapper just carries the already-resolved values into the providers.
 */

import type React from "react";
import { useMemo } from "react";

import { armScheduleClock } from "../../_logic/utils/schedule-clock";
import type { ScheduleBannerProps } from "../../_logic/actions/_shared/schedule-banner";
import type { EventLandingPagePublicRow } from "../../_logic/types/db-types";
import type { PublicPageInitialData } from "../../_logic/types/public-page-data";
import type { PageComponent } from "../../_types";
import { FocusedScheduleView } from "./FocusedScheduleView";
import { PublicPageProvider } from "./PublicPageProvider";
import type { PublicViewerAccess } from "./V2LandingPagePublicWrapper";

interface V2SchedulePageWrapperProps {
  /** The resolved landing page row (parent page; schedule items are event-level). */
  page: EventLandingPagePublicRow;
  /** Parsed layout_tree — passed through to initialize the builder store the renderers read. */
  components: PageComponent[];
  /** Event-level IANA timezone for time display + the .ics/now-line. NULL = floating time. */
  eventTimeZone: string | null;
  /** Server-resolved viewer access (identity + attendee FK + admin/manage flags). */
  viewerAccess: PublicViewerAccess;
  /** Server-fetched schedule + signup-count + viewer-registration data; seeds the provider tree. */
  initialData?: PublicPageInitialData | null;
}

/**
 * Mounts the public providers around the focused calendar.
 *
 * @param props - See V2SchedulePageWrapperProps.
 * @returns The focused schedule page wrapped in its providers.
 */
export function V2SchedulePageWrapper({
  page,
  components,
  eventTimeZone,
  viewerAccess,
  initialData = null,
}: V2SchedulePageWrapperProps): React.ReactElement {
  // Arm the schedule clock with the server-resolved admin flag before children render, so the
  // calendar's now-line honors any `?__now=` override for admins. Idempotent — see the same call in
  // V2LandingPagePublicWrapper for why this lives in the render body, not an effect.
  armScheduleClock(viewerAccess.isNowadaysAdmin);

  // Force schedule data to fetch (the focused page always needs it) while keeping the live banner
  // off — this route renders only the calendar, never the sticky banner.
  const scheduleBanner = useMemo<ScheduleBannerProps>(
    () => ({ enableSchedule: true, bannerEnabled: false, scheduleHref: null }),
    []
  );

  const backHref = `/${page.event_id}/landing-page/${page.slug}`;

  // Render
  return (
    <PublicPageProvider
      page={page}
      components={components}
      eventTimeZone={eventTimeZone}
      scheduleBanner={scheduleBanner}
      viewer={viewerAccess.viewer}
      attendeeId={viewerAccess.attendeeId}
      isOrganizer={viewerAccess.canManageEvent || viewerAccess.isNowadaysAdmin}
      canPreviewTestPublish={viewerAccess.canPreviewTestPublish}
      initialData={initialData}
    >
      <FocusedScheduleView pageName={page.name} backHref={backHref} />
    </PublicPageProvider>
  );
}
